import { z } from "zod";
import { responseSchema } from "../http.js";
/**
 * ═══════════════════════════════════════════════════════════
 * El reloj de recompra (membresía)
 *
 * Porte del `membership-clock.service.ts` de producción, con las mismas
 * reglas y los mismos nombres de estado:
 *
 * - El socio tiene un CICLO (30 días por defecto) que arranca en CADA
 *   orden validada. Mientras el ciclo no venza, su membresía está activa.
 * - `validUntil` es la ÚNICA verdad guardada. El estado (`active`,
 *   `expiring`, `expired`) se DERIVA al leer, nunca se persiste: un
 *   estado guardado se quedaría congelado en "active" al vencer, porque
 *   nada lo refrescaría.
 * - El reloj SOLO AVANZA: una recompra anticipada lo empuja hacia
 *   adelante; reprocesar una orden vieja no puede acortarlo.
 * - La fecha de vencimiento se deriva de la fecha de VALIDACIÓN de la
 *   orden, no de "ahora": reintentar la misma orden da el mismo
 *   resultado y no regala días.
 *
 * Lo que producción NO tenía y aquí sí: el estado `none` para quien
 * nunca ha tenido una orden validada. Allá "sin fecha" y "vencido" eran
 * lo mismo, y la pantalla tenía que adivinar con "¿tiene alguna orden?"
 * para decidir entre "haz tu primera compra" y "haz tu recompra".
 * ═══════════════════════════════════════════════════════════
 */
export const DEFAULT_MEMBERSHIP_CYCLE_DAYS = 30;
export const DEFAULT_EXPIRING_SOON_DAYS = 7;
/**
 * Los puntos que hacen SOCIO a alguien (Anahí, 14-sep-2026): «mínimo al
 * mes tiene que generar 50 puntos». Con menos, ese mes es cliente: paga
 * precio de socio pero no cobra comisiones de su red.
 */
export const DEFAULT_ACTIVATION_VOLUME_POINTS = 50;
/** Cotas de cordura: ni todo el mundo vencido (0 días) ni activo para
 * siempre (10 años). Las mismas de producción. */
export const MIN_MEMBERSHIP_CYCLE_DAYS = 1;
export const MAX_MEMBERSHIP_CYCLE_DAYS = 365;
/**
 * La configuración del reloj. Vive DENTRO de los parámetros del plan de
 * compensación (`commission_params.membership`), como en producción
 * (`commission_params/default.membership`): el ciclo de recompra es una
 * regla del negocio que se edita sin deploy, igual que un porcentaje.
 */
export const membershipClockConfigSchema = z
    .object({
    cycleDays: z
        .number()
        .int("El ciclo se mide en días enteros")
        .min(MIN_MEMBERSHIP_CYCLE_DAYS, `El ciclo mínimo es ${MIN_MEMBERSHIP_CYCLE_DAYS} día`)
        .max(MAX_MEMBERSHIP_CYCLE_DAYS, `El ciclo máximo es ${MAX_MEMBERSHIP_CYCLE_DAYS} días`),
    /** Cuántos días antes de vencer se avisa "por vencer". */
    expiringSoonDays: z
        .number()
        .int("El aviso se mide en días enteros")
        .min(0, "El aviso no puede ser negativo")
        .max(MAX_MEMBERSHIP_CYCLE_DAYS - 1),
    /**
     * PV propios dentro del ciclo para ser SOCIO ACTIVO y cobrar
     * comisiones (Anahí, 14-sep-2026). Con valor por omisión: el plan ya
     * guardado no lo trae, y un plan sin este número es uno con la regla
     * de fábrica (50).
     */
    activationVolumePoints: z
        .number()
        .int("Los puntos son enteros")
        .min(0, "No puede ser negativo")
        .max(100000)
        .default(DEFAULT_ACTIVATION_VOLUME_POINTS),
})
    .refine((value) => value.expiringSoonDays < value.cycleDays, {
    message: "El aviso «por vencer» tiene que ser más corto que el ciclo",
    path: ["expiringSoonDays"],
});
export const DEFAULT_MEMBERSHIP_CLOCK_CONFIG = {
    cycleDays: DEFAULT_MEMBERSHIP_CYCLE_DAYS,
    expiringSoonDays: DEFAULT_EXPIRING_SOON_DAYS,
    activationVolumePoints: DEFAULT_ACTIVATION_VOLUME_POINTS,
};
// ── Socio activo ────────────────────────────────────────────
//
// «El socio es socio haciendo su recompra mensual; si no la hace, deja
// de ser socio y se convierte en cliente. Mínimo al mes tiene que
// generar 50 puntos. Y lo mismo el franquiciatario: solo el que hace su
// recompra y genera 50 puntos cobra comisiones de su red.» (Anahí,
// 14-sep-2026.)
//
// El estado se DERIVA, nunca se guarda: es la suma de los PV de sus
// compras validadas en los últimos `cycleDays` días contra
// `activationVolumePoints`. Un rol guardado "socio/cliente" se quedaría
// congelado al vencer el mes, igual que pasaba con el estado del reloj.
// El ROL de la cuenta no cambia: cliente y socio pagan el mismo precio;
// lo que cambia es si cobra.
export const partnerActivationSchema = z.object({
    /** `true` = este mes cobra comisiones de su red. */
    active: z.boolean(),
    /** Sus PV en la ventana. */
    volumePoints: z.number().int().min(0),
    /** Los que necesita. */
    required: z.number().int().min(0),
    cycleDays: z.number().int().min(1),
    /** Desde cuándo cuentan las compras (ahora − ciclo). */
    windowStart: z.iso.datetime(),
    /** La membresía anual vigente (14-sep): la otra condición para cobrar. */
    membershipActive: z.boolean(),
    membershipValidUntil: z.iso.datetime().nullable(),
});
/** La regla, en un solo sitio: ¿con estos puntos es socio activo? */
export function isPartnerActive(volumePoints, required) {
    return volumePoints >= required;
}
// ── El estado ───────────────────────────────────────────────
export const MEMBERSHIP_STATUSES = ["none", "active", "expiring", "expired"];
export const membershipStatusSchema = z.enum(MEMBERSHIP_STATUSES);
/** Las etiquetas de producción (`RelojRecompra.tsx`), más la de `none`. */
export const MEMBERSHIP_STATUS_LABELS = {
    none: "Sin membresía",
    active: "Activa",
    expiring: "Por vencer",
    expired: "Vencida",
};
/** Un producto dentro del paquete que arrancó el ciclo. */
export const membershipPackageProductSchema = z.object({
    productId: z.string(),
    name: z.string(),
    imageUrl: z.string().nullable(),
    quantity: z.number().int().min(1),
});
/**
 * "Tu paquete actual", tal como lo pinta el resumen: lo que trajo la
 * orden que arrancó el ciclo vigente, resuelto a productos con imagen.
 * Si la orden fue un paquete, se expande a sus productos; si fueron
 * productos sueltos, son ellos.
 */
export const membershipActivePackageSchema = z.object({
    name: z.string(),
    folio: z.string(),
    validatedAt: z.iso.datetime(),
    /** Unidades totales (suma de cantidades). */
    units: z.number().int().min(0),
    products: z.array(membershipPackageProductSchema),
});
/** Un paquete que la persona compró y se validó (para el franquiciatario). */
export const membershipAcquiredPackageSchema = z.object({
    orderId: z.string(),
    packageId: z.string(),
    name: z.string(),
    folio: z.string(),
    validatedAt: z.iso.datetime(),
    category: z.enum(["personal", "franchisee"]),
    imageUrl: z.string().nullable(),
    productCount: z.number().int().min(0),
    units: z.number().int().min(0),
});
// ── La MEMBRESÍA ANUAL (Anahí, 14-sep-2026) ─────────────────
//
// «La membresía es anual: te da acceso a tu backoffice, la que te va a
// permitir ganar dinero. Se paga cada año. El socio tiene que comprar
// membresía; si no, no puede cobrar comisiones. Si el cliente compra un
// paquete se le regala la membresía digital.»
//
// Es OTRO reloj, distinto del de recompra (30 días, activación por
// puntos): este dura 12 meses y lo arranca —o lo renueva— cualquier
// orden validada que traiga una membresía, un paquete o la franquicia.
// Renovar antes de vencer SUMA un año a la fecha de vencimiento, no lo
// reinicia desde hoy: nadie pierde los meses que ya pagó.
export const ANNUAL_MEMBERSHIP_MONTHS = 12;
/** Cuántos días antes de vencer se avisa (campana + correo). */
export const ANNUAL_MEMBERSHIP_EXPIRING_SOON_DAYS = 30;
export const ANNUAL_MEMBERSHIP_SOURCES = ["membership", "package", "franchise", "manual"];
export const annualMembershipSourceSchema = z.enum(ANNUAL_MEMBERSHIP_SOURCES);
export const annualMembershipSchema = z.object({
    status: membershipStatusSchema,
    /** Fin de la membresía. `null` = nunca ha tenido. */
    validUntil: z.iso.datetime().nullable(),
    startedAt: z.iso.datetime().nullable(),
    remainingDays: z.number().int().min(0),
    /** Con qué se ganó: compró la membresía, se la regaló un paquete, la franquicia, o el admin. */
    source: annualMembershipSourceSchema.nullable(),
    expiringSoonDays: z.number().int().min(0),
});
/** Un año más, a partir de lo que sea más tarde: hoy o el vencimiento vigente. */
export function extendAnnualMembership(current, paidAt) {
    const base = current && current.getTime() > paidAt.getTime() ? current : paidAt;
    const next = new Date(base.getTime());
    next.setUTCMonth(next.getUTCMonth() + ANNUAL_MEMBERSHIP_MONTHS);
    return next;
}
/** ¿Tiene membresía anual vigente en ese instante? */
export function hasAnnualMembershipAt(validUntil, at) {
    return Boolean(validUntil && validUntil.getTime() >= at.getTime());
}
/**
 * El reloj de UNA persona, tal como lo pinta su panel.
 *
 * `serverNow` viaja a propósito: la cuenta regresiva del navegador se
 * ancla al reloj del SERVIDOR, no al de la máquina del socio — un reloj
 * local atrasado no puede enseñar días de más.
 */
export const membershipClockSchema = z.object({
    status: membershipStatusSchema,
    /** Fin del ciclo vigente. `null` = nunca ha tenido una orden validada. */
    validUntil: z.iso.datetime().nullable(),
    /** Cuándo arrancó el ciclo vigente (la validación de la última orden). */
    startedAt: z.iso.datetime().nullable(),
    /** Días completos que faltan. `0` al vencer o sin membresía. */
    remainingDays: z.number().int().min(0),
    cycleDays: z.number().int().min(1),
    expiringSoonDays: z.number().int().min(0),
    /** La orden que arrancó el ciclo vigente. */
    lastOrderId: z.string().nullable(),
    lastOrderFolio: z.string().nullable(),
    /** El paquete (o primer producto) de esa orden: "tu paquete actual". */
    activePackageName: z.string().nullable(),
    /** La misma orden, resuelta a productos con imagen. */
    activePackage: membershipActivePackageSchema.nullable(),
    /** Todos los paquetes validados de la persona, del más reciente al más viejo. */
    acquiredPackages: z.array(membershipAcquiredPackageSchema),
    /** La membresía ANUAL (14-sep-2026): sin ella no se cobran comisiones. */
    annual: annualMembershipSchema,
    serverNow: z.iso.datetime(),
});
export const membershipClockResponseSchema = responseSchema(membershipClockSchema);
export const MEMBERSHIP_ROUTES = {
    mine: "/api/membership/me",
};
// ── La aritmética, compartida ───────────────────────────────
//
// Vive en el contrato porque la usan LOS DOS lados: el servidor para
// derivar el estado que responde, y la pantalla para repintar la cuenta
// regresiva cada segundo sin volver a preguntar. Si los dos calcularan
// distinto, el socio vería "Activa" con el contador en cero.
export const DAY_MS = 24 * 60 * 60 * 1000;
export function addDays(date, days) {
    return new Date(date.getTime() + days * DAY_MS);
}
/** Fin del ciclo: la fecha de validación + los días de ciclo. */
export function computeValidUntil(paidAt, cycleDays) {
    return addDays(paidAt, cycleDays);
}
/**
 * Estado derivado. `validUntil` es la única verdad; el estado se
 * calcula, no se guarda.
 */
export function deriveMembershipStatus(validUntil, now, expiringSoonDays) {
    if (!validUntil)
        return "none";
    const remainingMs = validUntil.getTime() - now.getTime();
    if (remainingMs <= 0)
        return "expired";
    if (remainingMs <= expiringSoonDays * DAY_MS)
        return "expiring";
    return "active";
}
/** Días COMPLETOS que faltan; nunca negativo. */
export function remainingWholeDays(validUntil, now) {
    if (!validUntil)
        return 0;
    return Math.max(0, Math.floor((validUntil.getTime() - now.getTime()) / DAY_MS));
}
/**
 * El reloj solo avanza. Una recompra anticipada lo empuja hacia adelante;
 * una orden vieja reprocesada (o un webhook que llega tarde) NO puede
 * acortar una membresía ya renovada por una compra posterior.
 */
export function shouldExtendMembership(current, next) {
    if (!current)
        return true;
    return next.getTime() >= current.getTime();
}
/**
 * Lo que queda, descompuesto para la cuenta regresiva. Con `deadline`
 * pasado o ausente, todo en cero.
 */
export function splitTimeLeft(deadline, now) {
    const totalMs = deadline ? Math.max(0, deadline.getTime() - now.getTime()) : 0;
    const totalSeconds = Math.floor(totalMs / 1000);
    return {
        totalMs,
        days: Math.floor(totalSeconds / 86_400),
        hours: Math.floor((totalSeconds % 86_400) / 3_600),
        minutes: Math.floor((totalSeconds % 3_600) / 60),
        seconds: totalSeconds % 60,
    };
}
//# sourceMappingURL=membership.js.map